/**
 * Standalone page that renders FullAgenticChat with tools discovered
 * from local MCP servers. No WorkspaceProvider / CefSharp dependency.
 *
 * Route: /local
 * Auth:  Trimble Identity (TIDProvider + useAuth)
 * Agent: VITE_AGENT_ID env var
 */

import "./App.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  loadMcpServers,
  saveMcpServers,
  type McpServerConfig,
} from "./config/mcpServers";
import { discoverMcpTools } from "./services/mcpToolAdapter";
import {
  normalizeRuntimeTools,
  type RuntimeToolDef,
} from "./tools/runtimeTypes";
import { McpToolsPanel } from "./components/McpToolsPanel";
import {
  AuthenticationGuard,
  TIDProvider,
  useAuth,
} from "@trimble-oss/trimble-id-react";
import {
  AgenticFullChat,
  type OnBeforeRunProvider,
} from "@trimble-agentic-internal-npm-local/agentic-chat-react";
import tidClient, { TID_SCOPES } from "./components/tidClient";
import { resolveChatFeatures } from "./config/chatFeatures";

const CHAT_FEATURES = resolveChatFeatures(
  TID_SCOPES,
  import.meta.env.VITE_ENVIRONMENT === "dev",
);

/**
 * Inner component that lives inside TIDProvider so useAuth() works.
 */
function LocalAppInner() {
  const { getAccessTokenSilently } = useAuth();
  const agentId = import.meta.env.VITE_AGENT_ID as string | undefined;
  const environment = (import.meta.env.VITE_ENVIRONMENT ?? "prod") as
    | "prod"
    | "stage"
    | "dev";
  const chatEnvironment: "prod" | "stage" =
    environment === "dev" ? "stage" : environment;

  const [servers, setServers] = useState<McpServerConfig[]>(loadMcpServers);
  const [mcpTools, setMcpTools] = useState<Record<
    string,
    RuntimeToolDef
  > | null>(null);
  const [mcpError, setMcpError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Discover MCP tools
  const connectToServers = useCallback(
    async (serversToConnect: McpServerConfig[]) => {
      setIsConnecting(true);
      setMcpError(null);
      try {
        const tools = await discoverMcpTools(
          serversToConnect,
          getAccessTokenSilently,
        );
        setMcpTools(tools);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[LocalApp] MCP discovery failed:", msg);
        setMcpError(msg);
        setMcpTools({});
      } finally {
        setIsConnecting(false);
      }
    },
    [getAccessTokenSilently],
  );

  // Connect on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    connectToServers(servers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleServerChange = useCallback(
    (index: number, updated: McpServerConfig) => {
      setServers((prev) => {
        const next = [...prev];
        next[index] = updated;
        saveMcpServers(next);
        return next;
      });
    },
    [],
  );

  const handleAddServer = useCallback(() => {
    setServers((prev) => {
      const next = [
        ...prev,
        {
          name: "new-server",
          url: "http://localhost:3000/",
          type: "http" as const,
        },
      ];
      saveMcpServers(next);
      return next;
    });
  }, []);

  const handleRemoveServer = useCallback((index: number) => {
    setServers((prev) => {
      const next = prev.filter((_, i) => i !== index);
      saveMcpServers(next);
      return next;
    });
  }, []);

  const handleReconnect = useCallback(() => {
    connectToServers(servers);
  }, [connectToServers, servers]);

  const provideToken = useCallback(
    () => getAccessTokenSilently(),
    [getAccessTokenSilently],
  );

  const chatInputConfig = useMemo(
    () => ({ buttons: [] as never[], hideModelSelection: false }),
    [],
  );

  // Wire MCP tools as runTime tools (full definition + callback)
  const runTimeTools = useMemo(() => {
    if (!mcpTools) return {};
    const rt: Record<string, RuntimeToolDef> = {};
    for (const [name, tool] of Object.entries(mcpTools)) {
      rt[name] = {
        definition: tool.definition,
        callback: tool.callback,
        timeOutInMs: tool.timeOutInMs,
      };
    }
    return normalizeRuntimeTools(rt);
  }, [mcpTools]);

  const onBeforeRunConfig = useMemo<OnBeforeRunProvider>(
    () => async () => ({
      tools: {
        runTime: runTimeTools,
        global: {},
      },
    }),
    [runTimeTools],
  );

  // ── Error states ────────────────────────────────────────────────────

  if (!agentId) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div style={{ color: "#d32f2f", fontSize: "1.25rem" }}>
          Missing VITE_AGENT_ID
        </div>
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          Set <code>VITE_AGENT_ID</code> in your <code>.env.local</code> file to
          use the /local route.
        </div>
      </div>
    );
  }

  if (mcpTools === null) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div>Discovering MCP tools…</div>
        <div style={{ fontSize: "0.875rem", color: "#666" }}>
          Connecting to {servers.length} local MCP server
          {servers.length !== 1 ? "s" : ""}
        </div>
      </div>
    );
  }

  if (mcpError) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div style={{ color: "#d32f2f", fontSize: "1.25rem" }}>
          MCP Connection Error
        </div>
        <div style={{ maxWidth: 480, textAlign: "center" }}>{mcpError}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <McpToolsPanel
        servers={servers}
        tools={mcpTools ?? {}}
        onServerChange={handleServerChange}
        onAddServer={handleAddServer}
        onRemoveServer={handleRemoveServer}
        onReconnect={handleReconnect}
        isConnecting={isConnecting}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <AgenticFullChat
          provideToken={provideToken}
          environment={chatEnvironment}
          theme="light"
          chatInputConfiguration={chatInputConfig}
          runConfig={onBeforeRunConfig}
          agentId={agentId}
          features={CHAT_FEATURES}
        />
      </div>
    </div>
  );
}

/**
 * Outer wrapper that provides TIDProvider + AuthenticationGuard
 * so that LocalAppInner can use useAuth().
 */
function LocalApp() {
  return (
    <TIDProvider tidClient={tidClient}>
      <AuthenticationGuard renderComponent={<LocalAppInner />} />
    </TIDProvider>
  );
}

export default LocalApp;
